/**
 * ReferenceImageUploadPanel
 *
 * Grid of up to 4 reference image slots for Nano Banana 2 multi-image generation.
 * Uploads via /api/v2/assets/upload and inserts @REF_N mentions.
 * Max 100 lines.
 */
import apiClient from '@/lib/api-client';
import { ReferenceImageUploadSlot } from './ReferenceImageUploadSlot';

interface ReferenceSlot { id: string | null; previewUrl: string | null; }

interface Props {
  slots: ReferenceSlot[];
  onSlotsChange: (slots: ReferenceSlot[]) => void;
  onInsertMention: (tag: string) => void;
}

interface UploadResponse { id: string; preview_url?: string | null; }

export function ReferenceImageUploadPanel({ slots, onSlotsChange, onInsertMention }: Props) {
  const visibleCount = slots.length;

  const handleUpload = async (index: number, file: File) => {
    const localUrl = URL.createObjectURL(file);
    // Snapshot vor Upload — kein stale-closure Problem
    const withPreview = slots.map((s, i) => i === index ? { id: null, previewUrl: localUrl } : s);
    onSlotsChange(withPreview);

    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await apiClient.post<UploadResponse>('/api/v2/assets/upload', formData, {
        headers: { 'Content-Type': undefined },
      });
      if (!res.data?.id) throw new Error('Upload failed');
      // ID hinzufügen, Preview bleibt (basierend auf withPreview, nicht stale slots)
      onSlotsChange(withPreview.map((s, i) => i === index ? { id: res.data.id, previewUrl: localUrl } : s));
    } catch (err) {
      console.error('Reference image upload failed:', err);
      // Preview bleibt sichtbar, id=null → wird beim Submit ignoriert
    }
  };

  const handleRemove = (index: number) => {
    const updated = [...slots];
    updated[index] = { id: null, previewUrl: null };
    onSlotsChange(updated);
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Referenzbilder
      </label>
      <div className="grid grid-cols-4 gap-2">
        {Array.from({ length: visibleCount }).map((_, i) => (
          <ReferenceImageUploadSlot
            key={i}
            slot={slots[i]}
            index={i}
            tag={`@REF_${i + 1}`}
            onUpload={handleUpload}
            onRemove={handleRemove}
            onInsertMention={onInsertMention}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Tipp: Klicke auf @REF_N um es in die Anweisungen einzufügen
      </p>
    </div>
  );
}
